import {
  API_LOAN_RICHER_VALUES_FORM,
  API_LOAN_RICHER_VALUES_ORDER_BY_ID,
  API_LOAN_RICHER_VALUES_ORDER_DETAILS,
  API_LOAN_RICHER_VALUES_ORDERS,
  API_LOAN_RICHER_VALUES_REFERENCE_ENTITIES,
  API_LOAN_RICHER_VALUES_REFERENCE_INSPECTION_TYPES,
  API_LOAN_RICHER_VALUES_REFERENCE_LOAN_OFFICERS,
  API_LOAN_RICHER_VALUES_REFERENCE_REPORT_TYPES,
  API_LOAN_RICHER_VALUES_REFERENCE_USERS,
  API_LOAN_RICHER_VALUES_SUBMIT,
  API_LOAN_RICHER_VALUES_SUBMIT_DRAFT,
} from 'config'
import Api from 'services/api'

export enum RicherValuesOrderType {
  EVAL = 'EVAL',
  AVM = 'AVM',
  OVERSIGHT = 'OVERSIGHT',
  INSPECTION = 'INSPECTION',
}

export interface IRicherValuesAttachedFile {
  field: string
  fileKey: string
  name: string
}

export enum RicherValuesDownloadHistoryStatus {
  Failed = -1,
  Pending = 0,
  Completed = 1,
}

export enum RicherValuesOrderStatus {
  Draft = 'Draft',
  Payment = 'Payment',
  New = 'New',
  InProgress = 'In Progress',
  OnHold = 'On Hold',
  Completed = 'Completed',
  InRevision = 'In Revision',
  Cancelled = 'Cancelled',
}

export const RICHER_VALUES_ORDER_STATUS_LABELS: Record<RicherValuesOrderStatus, string> = {
  [RicherValuesOrderStatus.Draft]: 'Draft',
  [RicherValuesOrderStatus.Payment]: 'Payment',
  [RicherValuesOrderStatus.New]: 'New',
  [RicherValuesOrderStatus.InProgress]: 'In Progress',
  [RicherValuesOrderStatus.OnHold]: 'On Hold',
  [RicherValuesOrderStatus.Completed]: 'Completed',
  [RicherValuesOrderStatus.InRevision]: 'In Revision',
  [RicherValuesOrderStatus.Cancelled]: 'Cancelled',
}

export interface IRicherValuesSubmitResponse {
  success: boolean
  message?: string
  orderSaved?: boolean
  order?: IRicherValuesOrder
  data?: {
    payment_link?: string
    intake_token?: string
    intake_form_link?: string
  }
  errors?: Record<string, string[] | string> | string[]
}

export interface IRicherValuesOrder {
  id: number
  loanNumber: number
  orderType: string
  intakeToken: string
  status?: RicherValuesOrderStatus
  dealId?: string
  reportPdfFileKey?: string
  data: {
    payment_link?: string
    intake_token?: string
    intake_form_link?: string
    deal_id?: string
    dealId?: string
    [key: string]: any
  }
  createdBy: string
  createdAt: string
  lastSyncedAt?: string | null
}

export interface IRicherValuesWizardStep {
  id: string
  title: string
  groups: string[]
  isReview?: boolean
}

export interface IRicherValuesFormSchema {
  wizardSteps: IRicherValuesWizardStep[]
  groups: Record<string, string[]>
  inputs: Record<string, any>
  toggleFields: string[]
  currencyFields: string[]
  fileFields: { key: string; title: string; required?: boolean; accept?: string }[]
  appendFilesAsArray: boolean
  hasContacts: boolean
  lineItems?: {
    key: string
    fields: { key: string; title: string; type?: string }[]
  }
  excludeFields?: { field: string; whenField: string; whenValueIn: string[] }[]
  requiredWhen?: { field: string; whenField: string; whenValueIn: string[] }[]
}

export const getRicherValuesOrders = (params: { force?: boolean } = {}) => {
  const query: Record<string, string> = {}
  if (params.force) query.force = 'true'
  return Api.get(API_LOAN_RICHER_VALUES_ORDERS, query)
}

export const getRicherValuesOrderById = (orderId: number) => {
  return Api.get(API_LOAN_RICHER_VALUES_ORDER_BY_ID, {}, { orderId })
}

export const getRicherValuesOrderDetails = (intakeToken: string) => {
  return Api.get(API_LOAN_RICHER_VALUES_ORDER_DETAILS, {}, { intakeToken })
}

export const getRicherValuesOrderForm = async (
  orderType: RicherValuesOrderType,
): Promise<IRicherValuesFormSchema | null> => {
  const res = await Api.get(API_LOAN_RICHER_VALUES_FORM, {}, { orderType: encodeURIComponent(orderType) })
  return res?.data ?? res ?? null
}

export const getRicherValuesLoanOfficers = () => Api.get(API_LOAN_RICHER_VALUES_REFERENCE_LOAN_OFFICERS)

export const getRicherValuesUsers = () => Api.get(API_LOAN_RICHER_VALUES_REFERENCE_USERS)

export const getRicherValuesEntities = () => Api.get(API_LOAN_RICHER_VALUES_REFERENCE_ENTITIES)

export const getRicherValuesReportTypes = (orderType: RicherValuesOrderType) =>
  Api.get(API_LOAN_RICHER_VALUES_REFERENCE_REPORT_TYPES, { orderType })

export const getRicherValuesInspectionTypes = (
  orderType: RicherValuesOrderType,
  reportType?: string,
) =>
  Api.get(API_LOAN_RICHER_VALUES_REFERENCE_INSPECTION_TYPES, {
    orderType,
    ...(reportType ? { reportType } : {}),
  })

export const submitRicherValuesOrder = (orderType: RicherValuesOrderType, formData: FormData) => {
  formData.append('order_type', orderType)
  return Api.postForm(API_LOAN_RICHER_VALUES_SUBMIT, formData)
}

export const submitRicherValuesDraftOrder = (orderId: number, formData: FormData) => {
  return Api.postForm(API_LOAN_RICHER_VALUES_SUBMIT_DRAFT, formData, { orderId })
}

export const updateRicherValuesOrder = (intakeToken: string, formData: FormData) => {
  return Api.postForm(API_LOAN_RICHER_VALUES_ORDER_DETAILS, formData, { intakeToken }, { method: 'put' })
}
